import React, { useMemo, useState } from "react";
import {
  Col,
  Row,
  Input,
  Button,
  Layout,
  Popover,
  Select,
  Table,
  Pagination,
  Modal,
} from "antd";
import { FaSearch, FaFilter, FaCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

// const ASSET_TYPES = [
//   { value: "IMAGE", label: "IMAGE" },
//   { value: "VIDEO", label: "VIDEO" },
//   { value: "PDF", label: "PDF" },
// ];

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Pending", label: "Pending" },
  { value: "Revoked", label: "Revoked" },
];

const ASSET_SHARING_DUMMY = [
  {
    id: "AS-001",
    assetName: "Leadership Masterclass Deck",
    sharedWith: "Alex Harper",
    role: "Trainer",
    status: "Active",
    date: "04/22/2026",
  },
  {
    id: "AS-002",
    assetName: "Client Communication Handbook",
    sharedWith: "Dr. Larry Montgomery, Sr",
    role: "Consultant",
    status: "Pending",
    date: "04/20/2026",
  },
  {
    id: "AS-003",
    assetName: "Sales Enablement Video",
    sharedWith: "Orla Noble",
    role: "Trainer",
    status: "Active",
    date: "04/18/2026",
  },
  {
    id: "AS-004",
    assetName: "Workshop Activity Sheet",
    sharedWith: "Quyn Jackson",
    role: "Consultant",
    status: "Revoked",
    date: "04/16/2026",
  },
  {
    id: "AS-005",
    assetName: "Negotiation Playbook",
    sharedWith: "Cora Colon",
    role: "Trainer",
    status: "Active",
    date: "04/14/2026",
  },
];

function AssetManagement() {
  const navigate = useNavigate();
  const [filterOpen, setFilterOpen] = useState(false);
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: 10,
    totalDocs: ASSET_SHARING_DUMMY.length,
    totalPages: 1,
  });
  const [filter, setFilter] = useState({
    keyword: "",
    type: undefined,
    status: undefined,
  });

  const filteredAssets = useMemo(() => {
    return ASSET_SHARING_DUMMY.filter((item) => {
      const keyword = filter.keyword?.trim().toLowerCase();
      const keywordMatched = !keyword
        || item.sharedWith.toLowerCase().includes(keyword)
        || item.id.toLowerCase().includes(keyword);
      const roleMatched = !filter.type || item.role === filter.type;
      const statusMatched = !filter.status || item.status === filter.status;
      return keywordMatched && roleMatched && statusMatched;
    });
  }, [filter.keyword, filter.type, filter.status]);

  const paginatedAssets = useMemo(() => {
    const start = (paginationConfig.pageNumber - 1) * paginationConfig.limit;
    return filteredAssets.slice(start, start + paginationConfig.limit);
  }, [filteredAssets, paginationConfig.pageNumber, paginationConfig.limit]);

  const startIndex = (paginationConfig.pageNumber - 1) * paginationConfig.limit + 1;
  const endIndex = Math.min(
    startIndex + paginationConfig.limit - 1,
    filteredAssets.length
  );
  const messageRecord = `Showing records ${filteredAssets.length ? endIndex : 0} of ${filteredAssets.length}`;

  const itemRender = (_, type, originalElement) => {
    if (type === "prev") return <button type="button">Previous</button>;
    if (type === "next") return <button type="button">Next</button>;
    return originalElement;
  };

  const handlePageChange = (page) => {
    setPaginationConfig((prev) => ({ ...prev, pageNumber: page }));
  };

  const handleLimitChange = (limit) => {
    setPaginationConfig((prev) => ({ ...prev, limit, pageNumber: 1, totalDocs: filteredAssets.length }));
  };

  const handleSearch = () => {
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const handleFilterApply = () => {
    setFilterOpen(false);
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const resetFilter = () => {
    setFilter({ keyword: "", type: undefined, status: undefined });
    setFilterOpen(false);
    setPaginationConfig((prev) => ({ ...prev, pageNumber: 1 }));
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: "Trainers",
      dataIndex: "sharedWith",
      key: "sharedWith",
      width: 200,
      ellipsis: true,
    },
    {
      title: "Role (Trainer / Consultant)",
      dataIndex: "role",
      key: "role",
      width: 220,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status) => (
        <span className={`assetShareStatus assetShareStatus${String(status).replace(/\s+/g, "")}`}>
          {status}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 130,
    },
    {
      title: "Action",
      key: "action",
      width: 190,
      fixed: "right",
      render: () => (
        <Button
          className="allowPermissionBtn"
          type="primary"
          size="middle"
          onClick={() => setPermissionModalOpen(true)}
        >
          Allow Permission
        </Button>
      ),
    },
  ];

  const filterContent = (
    <div className="filterDropdown">
      <div>
        <p className="mainLabel" style={{ padding: "10px" }}>Filter</p>
      </div>
      <hr style={{ margin: 0 }} />
      <div className="filterDropdownBody">
        <p className="mainLabel">Role</p>
        <Select
          size="large"
          className="filterSelectBox"
          placeholder="Select Role"
          value={filter.type}
          onChange={(v) => setFilter((f) => ({ ...f, type: v }))}
          style={{ width: "100%", marginBottom: 10 }}
          allowClear
          options={[
            { value: "Trainer", label: "Trainer" },
            { value: "Consultant", label: "Consultant" },
          ]}
        />
        <p className="mainLabel">Status</p>
        <Select
          size="large"
          className="filterSelectBox"
          placeholder="Select Status"
          value={filter.status}
          onChange={(v) => setFilter((f) => ({ ...f, status: v }))}
          style={{ width: "100%", marginBottom: 10 }}
          allowClear
          options={STATUS_OPTIONS}
        />
        <Button
          type="primary"
          shape="round"
          block
          size="large"
          style={{ marginBottom: "10px" }}
          className="mainButton primaryButton"
          onClick={handleFilterApply}
        >
          Apply
        </Button>
        <Button
          type="primary"
          shape="round"
          block
          size="large"
          className="mainButton primaryButton2"
          onClick={resetFilter}
        >
          Clear All
        </Button>
      </div>
    </div>
  );

  return (
    <Layout className="configuration">
      <div className="boxDetails2">
        <Row style={{ padding: "10px 20px" }}>
          <Col xs={24} md={12} style={{ display: "flex", alignItems: "center" }}>
            <h1 className="pageTitle">Asset Sharing</h1>
          </Col>
          <Col
            xs={24}
            md={12}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="primary"
              shape="round"
              size="large"
              style={{ padding: "8px 40px", height: "auto" }}
              className="loginButton"
              onClick={() => navigate("/asset-management/add")}
            >
              Add Asset
            </Button>
          </Col>
        </Row>

        <Row style={{ padding: "10px 20px" }}>
          <Col xs={24} md={12}>
            <h5 style={{ display: "inline", fontSize: 16 }}>Show : </h5>
            <Select
              size="large"
              className="chartSelectBox"
              value={paginationConfig.limit}
              onChange={handleLimitChange}
              style={{ width: 70, textAlign: "left" }}
              options={[
                { value: 10, label: "10" },
                { value: 20, label: "20" },
                { value: 30, label: "30" },
                { value: 40, label: "40" },
                { value: 50, label: "50" },
              ]}
            />
            &emsp;
            <h5 style={{ display: "inline", fontSize: 16 }}>Entries</h5>
          </Col>
          <Col
            xs={24}
            md={12}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Popover
              content={filterContent}
              trigger="click"
              open={filterOpen}
              onOpenChange={setFilterOpen}
              placement="bottomRight"
              arrow={false}
            >
              <Button
                style={{
                  padding: "8px 11px",
                  height: "auto",
                  borderRadius: "50px",
                  backgroundColor: "#00a79d",
                }}
              >
                <FaFilter style={{ fontSize: 16, color: "white" }} />
              </Button>
            </Popover>
            &emsp;
            <Input
              style={{ width: "250px" }}
              className="mainInput dashInput"
              placeholder="Search by ID or shared with"
              value={filter.keyword}
              onChange={(e) => setFilter((f) => ({ ...f, keyword: e.target.value }))}
              onPressEnter={handleSearch}
              suffix={
                <FaSearch
                  style={{ color: "#2484c6", fontSize: 16, cursor: "pointer" }}
                  onClick={handleSearch}
                />
              }
            />
          </Col>
        </Row>

        <Row style={{ padding: 20, overflow: "auto" }}>
          <Table
            className="styledTable assetSharingTable"
            dataSource={paginatedAssets}
            columns={columns}
            rowKey="id"
            pagination={false}
            scroll={{ x: 980 }}
          />
        </Row>
        <Row style={{ padding: "10px 20px" }}>
          <Col xs={24} md={12}>
            <p>{messageRecord}</p>
          </Col>
          <Col
            xs={24}
            md={12}
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            <Pagination
              className="styledPagination"
              current={paginationConfig.pageNumber}
              pageSize={paginationConfig.limit}
              total={filteredAssets.length}
              onChange={handlePageChange}
              itemRender={itemRender}
              showSizeChanger={false}
            />
          </Col>
        </Row>
        <br />
      </div>
      <Modal
        centered
        open={permissionModalOpen}
        onCancel={() => setPermissionModalOpen(false)}
        footer={null}
        className="assetPermissionModal"
        destroyOnClose
      >
        <div className="assetPermissionModalBody">
          <FaCheckCircle className="assetPermissionSuccessIcon" />
          <h3 className="assetPermissionTitle" style={{color:"#2484c6"}}>Access Granted Successfully</h3>
          <p className="assetPermissionSubtitle">
            Permission has been enabled for this asset.
          </p>
          <Button
            type="primary"
            className="allowPermissionBtn loginButton"
            onClick={() => setPermissionModalOpen(false)}
          >
            OK
          </Button>
        </div>
      </Modal>
      <br />
      <br />
    </Layout>
  );
}

export default AssetManagement;
